import { captureException } from "@client/telemetry/sentry";
import React from "react";

interface ErrorBoundaryRenderData {
  error: Error;
  resetError: () => void;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  // When this value changes while an error is shown, the error is cleared
  // and the children render again. Has no effect on healthy children (unlike
  // a `key`, which would remount them).
  resetOnChange?: unknown;
  fallback:
    | React.ReactElement
    | ((errorData: ErrorBoundaryRenderData) => React.ReactElement);
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
    });
    this.setState({ error });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (
      this.state.error != null &&
      prevProps.resetOnChange !== this.props.resetOnChange
    ) {
      this.resetError();
    }
  }

  resetError = (): void => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return typeof this.props.fallback === "function"
      ? this.props.fallback({ error, resetError: this.resetError })
      : this.props.fallback;
  }
}

function ErrorMessage({
  error,
  reset: _,
}: {
  error: Error;
  reset: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="text-red-500 p-5">
        Something went wrong on our end. We've been notified and will take a
        look at what went wrong. In the meantime, you can refresh the page and
        try again.
      </div>
      <div className="text-red-500 p-5">Error: {error.message}</div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withErrorBoundary<P extends Record<string, any>>(
  WrappedComponent: React.ComponentType<P>,
) {
  function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary
        fallback={({ error, resetError }) => (
          <ErrorMessage error={error} reset={resetError} />
        )}
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName ?? WrappedComponent.name ?? "Component"})`;
  return WithErrorBoundary;
}
